import React, { useEffect, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import { useState as useHState } from "@hookstate/core";
import { Box, Grid, GridItem, VStack, useColorModeValue, useDisclosure } from "@chakra-ui/react";
import { jsPDF } from "jspdf";
import PreviewCertificate, { Config, PreviewCertificateType } from "../../components/certificate/PreviewCertificate";
import supabase from "../../configs/Supabase";
import Loading from "../../components/loading/Loading";
import { NotFound } from "../../components/notFound/NotFound";
import { useOrganizationConfig } from "../../context/OrganizationConfigContext";

import styles from "../../styles/components/certificate.module.css";
import { ElementsType } from "../../components/certificate/DrawingSheet";
import { setWait } from "../../commonFunctions/commonFunctions";

import { CertificateHeader, HEADER_HEIGHT } from "../../components/displayCertificate/CertificateHeader";
import { ActionsCard } from "../../components/displayCertificate/ActionsCard";
import { MobileActionBar } from "../../components/displayCertificate/MobileActionBar";
import { CredentialTitle } from "../../components/displayCertificate/CredentialTitle";
import { MetadataGrid } from "../../components/displayCertificate/MetadataGrid";
import { ShareFollowFooter } from "../../components/displayCertificate/ShareFollowFooter";
import { VerifyCertificateModal } from "../../components/displayCertificate/VerifyCertificateModal";

export interface ImageElementsHolderType {
	[key:string]: HTMLImageElement
}

// Issue date as entered on the certificate, falling back to the event start date
const getIssueDate = (certificateData: any): string | undefined => {
	const metadata = certificateData.extra_metadata || {};
	const issueDate = metadata['Date of Issue'] || metadata['Issue Date'] || metadata.date;
	if (issueDate) return issueDate;
	if (certificateData.event_start_date) {
		return new Date(certificateData.event_start_date).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
	}
	return undefined;
};

// Participant ID column from the uploaded sheet, matched loosely ("Participant ID", "participant_id", etc.)
const getParticipantId = (certificateData: any): string | undefined => {
	const metadata = certificateData.extra_metadata || {};
	const key = Object.keys(metadata).find(k => k.toLowerCase().replace(/[^a-z]/g, '') === 'participantid');
	return key ? metadata[key] : undefined;
};

// Extract org name and subtitle from "OrgName | Subtitle" format
const parseOrgName = (orgName?: string): { name: string; subtitle?: string } => {
	if (!orgName) return { name: "Unknown Organization" };
	const parts = orgName.split("|");
	return {
		name: parts[0]?.trim() || orgName,
		subtitle: parts[1]?.trim(),
	};
};

export const DisplayCertificate = () => {
	//@ts-ignore
	const { id } = useParams();
	const pageBg = useColorModeValue('gray.50', 'gray.800');
	const cardBg = useColorModeValue('white', 'gray.700');
	const cardBorderColor = useColorModeValue('gray.200', 'gray.600');
	const canvasBg = useColorModeValue('gray.100', 'gray.800');
	const [certificateData, setCertificateData] = useState<any>(null);
	const [isLoading, setIsLoading] = useState(true);
	const [isExportLoading, setIsExportLoading] = useState(false);
	const [isDownloadImageLoading, setIsDownloadImageLoading] = useState(false);
	const [imageLeftToRender, setImageLeftToRender] = useState(0)
	const [imageElementsHolder, setImageElementsHolder] = useState<ImageElementsHolderType>({})
	const verifyModal = useDisclosure();

	const elementsData = useHState<ElementsType | null>(null);
	const configData = useHState<Config | null>(null);

	const previewCertificateRef = useRef<PreviewCertificateType>(null)
	const { getVerificationUrl, config: orgConfig, updateCertificateId } = useOrganizationConfig();

	// Update certificate ID in context when it becomes available (for fallback lookup)
	useEffect(() => {
		if (id) {
			updateCertificateId(id);
		}
	}, [id, updateCertificateId]);

	const fetchData = async () => {
		const { data, error } = await supabase.rpc("get_certificate_data_for_certificate_creation", { certificate_id: id, certificate_short_id: id });

		if (error) {
			console.error('Error fetching certificate data:', error);
			setIsLoading(false);
			return;
		} else if (data && data.length > 0) {
			const certificateData = data[0];
			const templateUrlData = await fetch(
				`${ process.env.REACT_APP_TEMPLATE_URL || 'https://us-central1-gmc-testing.cloudfunctions.net/getTemplateUrlFromTemplateId' }?template_id=${certificateData.template_id}`
			);
			const templateUrlDataJson = await templateUrlData.json();
			setCertificateData({
				...certificateData,
				templateImageUrl: templateUrlDataJson.url.signedURL,
			});
			configData.set(certificateData?.config);
			elementsData.set(certificateData?.elements);

			setIsLoading(false);
		} else if (data && data.length === 0) {
			setIsLoading(false);
			return;
		} else {
			return;
		}
	};

	useEffect(() => {
		fetchData();
	// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [supabase]);

	const handleDownloadImage = async () => {
		setIsDownloadImageLoading(true)
		let base64Data = previewCertificateRef.current?.getBase64Image()
		if (base64Data) {
			let image = base64Data.replace("image/jpeg", "image/octet-stream");
			var link = document.createElement("a");
			link.download = `${id}.png`;
			link.href = image;
			link.click();
		}
		await setWait(1000);
		setIsDownloadImageLoading(false)
	};

	const handleDownloadPDF = async () => {
		setIsExportLoading(true);
		let base64Data = previewCertificateRef.current?.getBase64Image()
		if (base64Data) {
			let canvasImg = new Image();
			canvasImg.crossOrigin = "Anonymous";
			canvasImg.src = base64Data;
			canvasImg.onload = async () => {
				const doc = new jsPDF({
					unit: "px",
					format: [canvasImg.width, canvasImg.height],
					orientation: canvasImg.height > canvasImg.width ? "portrait" : "landscape",
				});
				doc.addImage(canvasImg, 0, 0, canvasImg.width, canvasImg.height);
				doc.save(`${id}.pdf`);
			};
		}
		await setWait(1000);
		setIsExportLoading(false);
	};

	useEffect(() => {
		if (!certificateData || !orgConfig) return; // Wait for config to be loaded

		let imageObjectsHolder:HTMLImageElement[] = []
		const verificationUrl = getVerificationUrl(certificateData.id);

		Object.keys(elementsData).map((key, index) => {
			if (key === "gmc_link") {
				// @ts-ignore
				elementsData[key]["data"]["text"].set("Verify at " + verificationUrl);
			} else if (key === "gmc_qr") {
				// @ts-ignore
				elementsData[key]["data"]["url"].set(verificationUrl);
			//@ts-ignore
			} else if (key === "certificateId") {
				// @ts-ignore
				elementsData[key]["data"]["text"].set(certificateData.id);
			//@ts-ignore
			} else if (elementsData[key]["type"].get() === "image") {
				// @ts-ignore
				elementsData[key]["data"]["url"].set(certificateData?.extra_metadata[elementsData.get()[key]['header']]);
				imageObjectsHolder[index] = new Image();
				imageObjectsHolder[index].crossOrigin = "Anonymous";
				//@ts-ignore
				imageObjectsHolder[index].src = certificateData?.extra_metadata[elementsData.get()[key]['header']];
				imageObjectsHolder[index].name = key;
				setImageLeftToRender(num => {
					return num + 1
				})
				imageObjectsHolder[index].addEventListener("load", (el:Event) => {
					// @ts-ignore
					elementsData[(el.target as HTMLImageElement).getAttribute('name')]["data"]["url"].set(el.target.getAttribute('src'));
					setImageElementsHolder( vals => {
						return {
							...vals,
							[(el.target as HTMLImageElement).getAttribute('name') as string]: el.target as HTMLImageElement
						}
					})
					setImageLeftToRender(num => {
						return num - 1
					})
				});
			} else {
				// @ts-ignore
				elementsData[key]["data"]["text"].set(certificateData?.extra_metadata[elementsData.get()[key]['header']]);
			}

			return true
		});

		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [certificateData, orgConfig, getVerificationUrl]);

	if (isLoading) {
		return <Loading />;
	}

	if (certificateData) {
		const bg = certificateData?.templateImageUrl;
		const isIdCard = certificateData?.extra_metadata['type'] === 'ID-Card';

		let linkedinUrl = new URL('https://www.linkedin.com/profile/add?startTask=CERTIFICATION_NAME')
		if (certificateData.event_name) {
			linkedinUrl.searchParams.append('name', certificateData.event_name)
		}
		if (certificateData.linkedin_company_id) {
			linkedinUrl.searchParams.append('organizationId', certificateData.linkedin_company_id)
		}
		if (certificateData.event_start_date || certificateData.extra_metadata.date) {
			let eventDate = new Date(certificateData.extra_metadata.date || certificateData.event_start_date)
			linkedinUrl.searchParams.append('issueYear', eventDate.getFullYear().toString())
			linkedinUrl.searchParams.append('issueMonth', eventDate.getMonth().toString())
		}
		if (certificateData.id) {
			linkedinUrl.searchParams.append('certUrl', window.location.href)
		}
		if (certificateData.id) {
			linkedinUrl.searchParams.append('certId', certificateData.id)
		}

		const { name: orgName, subtitle: orgSubtitle } = parseOrgName(certificateData.organization_name);

		const issueDate = getIssueDate(certificateData);
		const recipientName = certificateData.extra_metadata?.name;

		// Canvas box is width-driven at the template's aspect ratio (A4 landscape fallback)
		const renderedDims = configData.value?.rendered_dimensions;
		const certRatio = renderedDims && renderedDims.width > 0 && renderedDims.height > 0
			? renderedDims.width / renderedDims.height
			: 1.414;

		const actionProps = {
			onDownload: handleDownloadImage,
			onDownloadPdf: handleDownloadPDF,
			onLinkedIn: () => window.open(linkedinUrl.toString(), "_blank"),
			onVerify: verifyModal.onOpen,
			isDownloadLoading: isDownloadImageLoading,
			isPdfLoading: isExportLoading,
			isDisabled: imageLeftToRender !== 0,
			isIdCard,
		};

		return (
			<Box bg={pageBg} minH="100vh">
				<CertificateHeader
					orgName={orgName}
					orgSubtitle={orgSubtitle}
					orgLogoUrl={orgConfig?.logoUrl || certificateData.organization_logo_url}
				/>

				<Box maxW="1200px" mx="auto" pt={{ base: "80px", md: "96px" }} px={{ base: 4, md: 6 }} pb={{ base: "96px", lg: 12 }}>
					<CredentialTitle
						programName={certificateData.event_name}
						recipientName={recipientName}
						orgName={orgName}
						issueDate={issueDate}
					/>

					{/* Mobile: certificate → actions → details. Desktop: sidebar spans both rows */}
					<Grid
						mt={{ base: 5, md: 6 }}
						gap={{ base: 4, md: 6 }}
						templateColumns={{ base: "minmax(0, 1fr)", lg: "minmax(0, 1fr) 320px" }}
						templateAreas={{
							base: `"certificate" "sidebar" "details"`,
							lg: `"certificate sidebar" "details sidebar"`,
						}}
					>
						<GridItem area="certificate" minW="0">
							<Box bg={cardBg} borderWidth={1} borderColor={cardBorderColor} rounded="xl" shadow="sm" p={{ base: 2, md: 4 }}>
								<Box
									bg={canvasBg}
									rounded="lg"
									overflow="hidden"
									mx="auto"
									w="100%"
									maxW={{ base: "100%", lg: `calc(70vh * ${certRatio})` }}
								>
									<div className={styles.artboard} id="preview_certificate" style={{ aspectRatio: `${certRatio}` }}>
										<PreviewCertificate
											ref={previewCertificateRef}
											configData={configData}
											elementsData={elementsData}
											bg={bg}
											imageLeftToRender={imageLeftToRender}
											isWhiteLabeled={certificateData?.is_white_labeled}
											imageElementsHolder={imageElementsHolder}
											orgConfig={orgConfig}
										/>
									</div>
								</Box>
							</Box>
						</GridItem>

						<GridItem area="sidebar" minW="0">
							<VStack spacing={4} align="stretch" position={{ lg: "sticky" }} top={{ lg: `calc(${HEADER_HEIGHT.md} + 24px)` }}>
								<Box display={{ base: "none", lg: "block" }}>
									<ActionsCard {...actionProps} />
								</Box>
								<ShareFollowFooter
									shareUrl={window.location.href}
									orgName={orgName}
									socialLinks={orgConfig?.socialLinks}
								/>
							</VStack>
						</GridItem>

						<GridItem area="details" minW="0">
							<MetadataGrid
								recipientName={recipientName}
								certificateType={certificateData.extra_metadata?.certificateType}
								issueDate={issueDate}
								participantId={getParticipantId(certificateData)}
								programName={certificateData.event_name}
								organizationName={orgName}
							/>
						</GridItem>
					</Grid>
				</Box>

				<MobileActionBar {...actionProps} />

				<VerifyCertificateModal
					isOpen={verifyModal.isOpen}
					onClose={verifyModal.onClose}
					recipientName={recipientName}
					programName={certificateData.event_name}
					issueDate={issueDate}
					certificateId={certificateData.id}
					issuedBy={orgName}
				/>
			</Box>
		);
	}

	return <NotFound />;
};
